import {json, type LoaderArgs} from '@shopify/remix-oxygen';

import {useLoaderData} from '@remix-run/react';
import invariant from 'tiny-invariant';
import {CACHE_LONG, routeHeaders} from '~/data/cache';
import {metaObjectQuery} from '~/data/fragments';
import {
  aggregateFields,
  defaultSizableImage,
  getSiteDetails,
  processArticle,
} from '~/utils';
import {useGetContext} from '~/hooks/useGetContext';
import NavbarSection from '~/components/Sections/NavbarSection';
import storefrontFetcher, {fetchSite} from '~/lib/storefrontFetcher';
import * as _ from 'lodash';
import * as Icon from 'react-feather';
import {seoPayload} from '~/lib/seo.server';
import {renderMarkDown} from '~/lib/utils';

export const headers = routeHeaders;

export async function loader({request, params, context}: LoaderArgs) {
  invariant(params.blogHandle, 'Missing blog handle');

  const site = await fetchSite({context});

  const {articles} = await getSiteDetails(context, site);

  const {storefront} = context;
  const {language, country} = storefront.i18n;
  const {metaobject: article} = await storefrontFetcher({
    context,
    query: metaObjectQuery(),
    variables: {
      metaobjectHandleInput: {
        handle: params.blogHandle,
        type: 'article',
      },
    },
  });
  if (
    !_.map(articles, (it) => _.get(it, 'id'))?.includes(_.get(article, 'id'))
  ) {
    throw new Response(null, {
      status: 404,
      statusText: 'Not Found',
    });
  }
  if (!article) {
    throw new Response(null, {status: 404});
  }
  const blog = _.get(aggregateFields([article]), '[0]') || {};
  const {title, media} = processArticle(blog, language, country);
  return json(
    {
      blog,
      language,
      country,
      seo: seoPayload.article({article: blog, title, url: request.url, media}),
    },
    {
      headers: {
        'Cache-Control': CACHE_LONG,
      },
    },
  );
}

export default function Page() {
  const {blog, language, country} = useLoaderData<typeof loader>();
  const {menu, logo, isAuthenticated} = useGetContext();

  const {
    title,
    media,
    content,
    content_MD,
    createdAt,
    author,
    isContentMarkdown,
  } = processArticle(blog, language, country);
  return (
    <>
      <NavbarSection
        menu={menu}
        logo={logo || ''}
        title={title || ''}
        isAuthenticated={isAuthenticated}
      />
      <div className="blog-details-area ptb-80">
        <div className="container">
          <div className="row">
            <div className="col-lg-12 col-md-12">
              <div className="blog-details-desc">
                <div className="article-image">
                  <img
                    src={
                      media ||
                      defaultSizableImage({title, width: 860, height: 700})
                    }
                    alt={title}
                  />
                </div>
                <div className="article-content">
                  <div className="entry-meta">
                    <ul>
                      <li>
                        <Icon.Clock /> {createdAt}
                      </li>
                      <li>
                        <Icon.User /> <a href="/">{author}</a>
                      </li>
                    </ul>
                  </div>

                  <h3>{title}</h3>
                </div>
                <div
                  dangerouslySetInnerHTML={{
                    __html: renderMarkDown(content_MD || ''),
                  }}
                  className="prose blog-content"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
